import React from "react";
import { Synonym } from "../types";
import { useLanguage } from "../contexts/LanguageContext";
import { useTranslations } from "../translations";
import "./SynonymCard.css";

interface SynonymCardProps {
  synonym: Synonym;
}

const SynonymCard: React.FC<SynonymCardProps> = ({ synonym }) => {
  const { language } = useLanguage();
  const t = useTranslations(language);

  const getTranslation = (lang: string) => {
    return synonym.translations.find((t) => t.language === lang);
  };

  const englishTranslation = getTranslation("en");
  const ukrainianTranslation = getTranslation("uk");

  const getFrequencyStars = (frequency: number) => {
    return "★".repeat(frequency) + "☆".repeat(5 - frequency);
  };

  return (
    <div className="synonym-card">
      <div className="synonym-header">
        <h3 className="synonym-word">{synonym.word}</h3>
        <div className="synonym-meta">
          <span className="part-of-speech">{synonym.part_of_speech}</span>
          <span className="register">{synonym.register}</span>
          <span className="frequency" title={`${t.frequency}: ${synonym.frequency}/5`}>
            {getFrequencyStars(synonym.frequency)}
          </span>
        </div>
      </div>

      <div className="translations">
        {englishTranslation && (
          <div className="translation">
            <span className="translation-lang">{t.translationEN}:</span>
            <span className="translation-text">{englishTranslation.text}</span>
          </div>
        )}
        {ukrainianTranslation && (
          <div className="translation">
            <span className="translation-lang">{t.translationUK}:</span>
            <span className="translation-text">{ukrainianTranslation.text}</span>
          </div>
        )}
      </div>

      {synonym.usage_notes && (
        <div className="usage-notes">
          <strong>{t.note}:</strong> {synonym.usage_notes}
        </div>
      )}

      {synonym.usage_examples.length > 0 && (
        <div className="usage-examples">
          <strong>{t.examples}:</strong>
          {synonym.usage_examples.map((example, index) => (
            <div key={index} className="usage-example">
              <p className="example-sentence">{example.german_sentence}</p>
              {example.english_translation && (
                <p className="example-translation">
                  {t.translationEN}: {example.english_translation}
                </p>
              )}
              {example.ukrainian_translation && (
                <p className="example-translation">
                  {t.translationUK}: {example.ukrainian_translation}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SynonymCard;
